import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { Severity } from 'Data/ViewModels/Core/Severity';
import { hasEmptyValue, hasNonEmptyValue, hasValue } from 'Lib/Any';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

enum QestLabCallbacks {
  /**
   * Allows QESTLab to issue a notification within the QEST Web App
   */
  Notify = 'qestWebAppNotify',
  /**
   * Allows QESTLab to check whether the QEST Web App is busy (to prevent navigation)
   */
  IsBusy = 'qestWebAppIsBusy',
}

/**
 * A hook that will register / unregister the callbacks we require for interaction with
 * QESTLab.
 *
 * This should only be called from the main app component.
 *
 * WebKitX has a memory leak when the same callback function is registered repeatedly,
 * so these should be registered once and unregistered when done.
 */
export function useQestLabCallbacks() {
  const dispatch = useDispatch();

  // Previously we would register a different isBusy callback when the busy state changed.
  // This can cause memory leaks with the current WebKitX, so for now we store the busy state in a global variable and have the callback reference that.
  const isBusy = useSelector(
    (state) =>
      state.admin.status?.syncing ||
      state.admin.status?.dirty ||
      state.agsProjectExport.pageHeader?.processing ||
      state.equipment.status?.synchronising ||
      state.equipment.status?.dirty ||
      state.worksheetEditor.status?.saving ||
      state.worksheetEditor.status?.dirty ||
      state.worksheetEditor.status?.uploadingImage ||
      state.worksheetEditor.status?.settingTestedByCheckedBy ||
      Object.values(state.worksheetGrid.worksheets).some((w) => w.status.dirty || w.status.saving) ||
      state.worksheetBatchUpdate.status.applyingUpdate ||
      state.worksheetBatchUpdate.status.checking
  );

  // Retrieving the userAgent via a selector allows us to use it as a dependency of a useEffect. The userAgent can't be used directly because
  // outer scope values like 'window.qest.userAgent'/'navigator.userAgent' aren't valid dependencies because mutating them doesn't re-render the component
  const userAgent = useSelector(() => window.qest.userAgent);

  useEffect(() => {
    window.qest.isBusy = isBusy;
  }, [isBusy]);

  useEffect(() => {
    if (hasValue(window.register)) {
      window.register!(QestLabCallbacks.IsBusy, () => window.qest.isBusy);
    }

    return () => {
      if (hasValue(window.unregister)) {
        window.unregister!(QestLabCallbacks.IsBusy);
      }
    };
  }, []);

  useEffect(() => {
    if (hasValue(window.register)) {
      window.register!(QestLabCallbacks.Notify, (message: string, severity: Severity, additionalDetails: string) => {
        dispatch(noticeActions.addNotice({ message, severity, additionalDetails }));
      });
    }

    return () => {
      if (hasValue(window.unregister)) {
        window.unregister!(QestLabCallbacks.Notify);
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (userAgent?.includes('[QFW]') && hasEmptyValue(window.qest.notify)) {
      window.qest.notify = (message: string, severity: Severity, additionalDetails: string) => {
        dispatch(noticeActions.addNotice({ message, severity, additionalDetails }));
      };
    }

    return () => {
      if (hasNonEmptyValue(window.qest.notify)) {
        window.qest.notify = undefined;
      }
    };
  }, [dispatch, userAgent]);
}
